import { useState, useEffect } from "react";
import { Bell } from "lucide-react";
import { dbGet, dbSet } from "../lib/db";

const NK = "reconectados_notifs";

const DEFAULT = {
  morning: { enabled: false, time: "08:00" },
  evening: { enabled: false, time: "21:00" },
};

// ms until next occurrence of HH:MM (today or tomorrow if already passed)
function msUntil(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const now    = new Date();
  const target = new Date();
  target.setHours(h, m, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 1);
  return target - now;
}

async function showNotif(title, body) {
  if (Notification?.permission !== "granted") return;
  try {
    const reg = await navigator?.serviceWorker?.getRegistration?.();
    if (reg) { reg.showNotification(title, { body, icon: "/icon-192.png", badge: "/icon-192.png" }); return; }
  } catch { /* fall through */ }
  try { new Notification(title, { body }); } catch { /* silently ignored */ }
}

const timers = [];

export function scheduleReminders(settings) {
  // Cancel previous timers
  timers.forEach(id => clearTimeout(id));
  timers.length = 0;

  if (settings?.morning?.enabled && settings.morning.time) {
    timers.push(setTimeout(
      () => showNotif("Mi Diario – Buenos días ☀️", "¿Cómo amaneciste? Haz tu check-in matutino."),
      msUntil(settings.morning.time)
    ));
  }
  if (settings?.evening?.enabled && settings.evening.time) {
    timers.push(setTimeout(
      () => showNotif("Mi Diario – Reflexión del día 🌙", "Es hora de tu check-in nocturno."),
      msUntil(settings.evening.time)
    ));
  }
}

export default function NotifSettings() {
  const [open, setOpen]       = useState(false);
  const [perm, setPerm]       = useState(() => Notification?.permission ?? "default");
  const [settings, setSettings] = useState(DEFAULT);

  useEffect(() => {
    dbGet(NK, DEFAULT).then(s => {
      const loaded = s && typeof s === "object" ? s : DEFAULT;
      setSettings(loaded);
      if (Notification?.permission === "granted") scheduleReminders(loaded);
    });
  }, []);

  const requestPerm = async () => {
    if (!("Notification" in window)) return;
    const result = await Notification.requestPermission();
    setPerm(result);
    if (result === "granted") scheduleReminders(settings);
  };

  const save = async (newSettings) => {
    setSettings(newSettings);
    await dbSet(NK, newSettings);
    if (Notification?.permission === "granted") scheduleReminders(newSettings);
  };

  const toggle = (slot) =>
    save({ ...settings, [slot]: { ...settings[slot], enabled: !settings[slot].enabled } });

  const setTime = (slot, val) =>
    save({ ...settings, [slot]: { ...settings[slot], time: val } });

  const anyActive = settings.morning.enabled || settings.evening.enabled;

  return (
    <div className="bg-white rounded-2xl border border-[#E4EBE6] mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#EDF4FF] flex items-center justify-center">
            <Bell size={18} className="text-[#5B8FCC]" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm text-[#1E2F23]" style={{ fontFamily: "Manrope" }}>
              Recordatorios
            </p>
            <p className="text-xs text-[#2C4238]">
              {anyActive
                ? `Activos · ${settings.morning.enabled ? settings.morning.time : ""} ${settings.evening.enabled ? settings.evening.time : ""}`.trim()
                : "Desactivados"}
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold text-[#6B9080]">
          {open ? "Cerrar" : "Configurar"}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-[#F0F5F2] pt-4 space-y-4 animate-fade-in">

          {/* Permission banner */}
          {perm !== "granted" && (
            <div className="bg-[#EDF4FF] rounded-xl p-3 flex items-start gap-3">
              <Bell size={16} className="text-[#5B8FCC] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-[#1E2F23] font-semibold mb-1">Permite notificaciones</p>
                <p className="text-xs text-[#2C4238] mb-2">Para recibir recordatorios de check-in.</p>
                <button
                  onClick={requestPerm}
                  className="px-4 py-1.5 bg-[#5B8FCC] text-white text-xs font-semibold rounded-xl"
                >
                  Permitir
                </button>
              </div>
            </div>
          )}

          {/* Morning */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#1E2F23]">☀️ Mañana</p>
              <input
                type="time"
                value={settings.morning.time}
                onChange={e => setTime("morning", e.target.value)}
                className="text-xs text-[#2C4238] bg-transparent border-none focus:outline-none mt-0.5 cursor-pointer"
              />
            </div>
            <button
              onClick={() => toggle("morning")}
              className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${settings.morning.enabled ? "bg-[#6B9080]" : "bg-[#E4EBE6]"}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.morning.enabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>

          {/* Evening */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-[#1E2F23]">🌙 Noche</p>
              <input
                type="time"
                value={settings.evening.time}
                onChange={e => setTime("evening", e.target.value)}
                className="text-xs text-[#2C4238] bg-transparent border-none focus:outline-none mt-0.5 cursor-pointer"
              />
            </div>
            <button
              onClick={() => toggle("evening")}
              className={`w-12 h-6 rounded-full transition-colors relative flex-shrink-0 ${settings.evening.enabled ? "bg-[#6B9080]" : "bg-[#E4EBE6]"}`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${settings.evening.enabled ? "translate-x-7" : "translate-x-1"}`} />
            </button>
          </div>

          <p className="text-xs text-[#4A6E5C] leading-relaxed">
            Los recordatorios se activan mientras el navegador esté abierto. Para notificaciones en segundo plano instala la app en tu pantalla de inicio.
          </p>
        </div>
      )}
    </div>
  );
}
